import { nestSubGroups, type NavigationEntry } from "@positive/ui";
import "@ui5/webcomponents-icons/dist/cart.js";
import "@ui5/webcomponents-icons/dist/heart.js";
import "@ui5/webcomponents-icons/dist/loan.js";
import "@ui5/webcomponents-icons/dist/customer-financial-fact-sheet.js";
import "@ui5/webcomponents-icons/dist/official-service.js";
import "@ui5/webcomponents-icons/dist/business-objects-experience.js";
import "@ui5/webcomponents-icons/dist/retail-store.js";
import "@ui5/webcomponents-icons/dist/building.js";

import type { HostMode } from "./identity/host";
import { assetsNavigation } from "../modules/assets/navigation";
import { cashBanksNavigation } from "../modules/cash/navigation";
import { masterDataCatalogNavigation } from "../modules/catalog/navigation";
import { deviceNavigation } from "../modules/device/navigation";
import { generalLedgerNavigation } from "../modules/gl/navigation";
import { hrNavigation } from "../modules/hr/navigation";
import { inventoryNavigation } from "../modules/inventory/navigation";
import { loyaltyNavigation } from "../modules/loyalty/navigation";
import { masterDataFinanceNavigation } from "../modules/masterdata/finance/navigation";
import { masterDataOrganizationNavigation } from "../modules/masterdata/organization/navigation";
import { msgNavigation } from "../modules/messaging/navigation";
import { procNavigation } from "../modules/proc/navigation";
import { scmNavigation } from "../modules/scm/navigation";
import { purchasingNavigation } from "../modules/purchasing/navigation";
import { platformPermissions } from "../modules/platform/permissions";
import { posNavigation } from "../modules/pos/navigation";
import { reportsNavigation } from "../modules/reports/navigation";
import { receivablesNavigation, salesNavigation } from "../modules/sales/navigation";
import { taxNavigation } from "../modules/tax/navigation";
import { crmNavigation } from "../modules/crm/navigation";
import { qualityNavigation } from "../modules/quality/navigation";
import { jewelNavigation } from "../modules/jewel/navigation";
import { fleetNavigation } from "../modules/fleet/navigation";
import { leaseNavigation } from "../modules/lease/navigation";
import { opsGroupIcon, opsNavigation } from "../modules/ops/navigation";
import { parkingNavigation } from "../modules/parking/navigation";
import { workNavigation } from "../modules/work/navigation";

/**
 * Navigation registry (ADR-0010/D-2). Each module contributes its entries with the permission (or `public`) that
 * gates them; the shell renders only what the user may open (deny by default, see `filterNavigation`). Screens tagged
 * with a `subGroup` move into that level-3 group of their module (UX11, `nestSubGroups`).
 */
const tenantGroups: NavigationEntry[] = [
  { key: "home", labelKey: "nav.home", icon: "home", path: "/", public: true },
  {
    key: "masterData",
    labelKey: "nav.masterData",
    icon: "database",
    module: "md",
    children: [...masterDataOrganizationNavigation, ...masterDataFinanceNavigation, ...masterDataCatalogNavigation],
  },
  {
    key: "generalLedger",
    labelKey: "nav.generalLedger",
    icon: "accounting-document-verification",
    module: "gl",
    children: [...generalLedgerNavigation],
  },
  {
    key: "inventory",
    labelKey: "nav.inventory",
    icon: "inventory",
    module: "inventory",
    children: [...inventoryNavigation],
  },
  {
    key: "sales",
    labelKey: "nav.sales",
    icon: "sales-order",
    module: "sales",
    children: [...salesNavigation],
  },
  {
    key: "pos",
    labelKey: "nav.pos",
    icon: "retail-store",
    module: "pos",
    children: [...posNavigation],
  },
  {
    key: "receivables",
    labelKey: "nav.receivables",
    icon: "customer-financial-fact-sheet",
    module: "finance",
    children: [...receivablesNavigation],
  },
  {
    key: "purchasing",
    labelKey: "nav.purchasing",
    icon: "cart",
    module: "purchasing",
    children: [...purchasingNavigation],
  },
  {
    key: "proc",
    labelKey: "nav.proc",
    icon: "official-service",
    module: "procurement",
    children: [...procNavigation],
  },
  {
    key: "scm",
    labelKey: "nav.scm",
    icon: "shipping-status",
    module: "supply_chain",
    children: [...scmNavigation],
  },
  {
    key: "assets",
    labelKey: "nav.assets",
    icon: "machine",
    module: "assets",
    children: [...assetsNavigation],
  },
  {
    key: "loyalty",
    labelKey: "nav.loyalty",
    icon: "heart",
    module: "loyalty",
    children: [...loyaltyNavigation],
  },
  {
    key: "cashBanks",
    labelKey: "nav.cashBanks",
    icon: "loan",
    module: "finance",
    children: [...cashBanksNavigation],
  },
  {
    key: "tax",
    labelKey: "nav.tax",
    icon: "official-service",
    module: "tax",
    children: [...taxNavigation],
  },
  {
    key: "reports",
    labelKey: "nav.reports",
    icon: "business-objects-experience",
    module: "gl",
    children: [...reportsNavigation],
  },
  {
    key: "hr",
    labelKey: "nav.hr",
    icon: "employee",
    module: "hr",
    children: [...hrNavigation],
  },
  {
    key: "crm",
    labelKey: "nav.crm",
    icon: "customer",
    module: "crm",
    children: [...crmNavigation],
  },
  {
    key: "quality",
    labelKey: "nav.quality",
    icon: "inspection",
    module: "quality",
    children: [...qualityNavigation],
  },
  {
    key: "lease",
    labelKey: "nav.lease",
    icon: "building",
    module: "leasing",
    children: [...leaseNavigation],
  },
  {
    key: "jewel",
    labelKey: "nav.jewel",
    icon: "retail-store",
    module: "jewellery",
    children: [...jewelNavigation],
  },
  {
    key: "parking",
    labelKey: "nav.parking",
    icon: "journey-depart",
    module: "parking",
    children: [...parkingNavigation],
  },
  {
    key: "fleet",
    labelKey: "nav.fleet",
    icon: "car-rental",
    module: "fleet",
    children: [...fleetNavigation],
  },
  {
    key: "ops",
    labelKey: "nav.ops",
    icon: opsGroupIcon,
    module: "operations",
    children: [...opsNavigation],
  },
  {
    key: "work",
    labelKey: "nav.work",
    icon: "task",
    module: "work",
    children: [...workNavigation],
  },
  {
    key: "devices",
    labelKey: "nav.devices",
    icon: "machine",
    module: "platform",
    children: [...deviceNavigation],
  },
  {
    key: "messaging",
    labelKey: "nav.messaging",
    icon: "discussion",
    module: "msg",
    children: [...msgNavigation],
  },
  {
    key: "platform",
    labelKey: "nav.platform",
    icon: "action-settings",
    module: "platform",
    children: [
      {
        key: "platform.users",
        labelKey: "nav.users",
        icon: "group",
        path: "/platform/users",
        permission: platformPermissions.userView,
      },
      {
        key: "platform.roles",
        labelKey: "nav.roles",
        icon: "role",
        path: "/platform/roles",
        permission: platformPermissions.roleView,
      },
      {
        key: "platform.settings",
        subGroup: "setup",
        labelKey: "nav.settings",
        icon: "settings",
        path: "/platform/settings/platform",
        permission: platformPermissions.settingsView,
      },
      {
        key: "platform.messagingSettings",
        subGroup: "setup",
        labelKey: "nav.messagingSettings",
        icon: "email",
        path: "/platform/settings/messaging",
        permission: platformPermissions.messagingSettingsView,
      },
      {
        key: "platform.approvals",
        labelKey: "nav.approvals",
        icon: "approvals",
        path: "/platform/approvals",
        permission: platformPermissions.approvalLogView,
      },
      {
        key: "platform.approvalTemplates",
        subGroup: "setup",
        labelKey: "nav.approvalTemplates",
        icon: "inbox",
        path: "/platform/approval-templates",
        permission: platformPermissions.approvalTemplateView,
      },
      {
        key: "platform.numberSeries",
        subGroup: "setup",
        labelKey: "nav.numberSeries",
        icon: "number-sign",
        path: "/platform/number-series",
        permission: platformPermissions.numberSeriesView,
      },
      {
        key: "platform.audit",
        labelKey: "nav.audit",
        icon: "history",
        path: "/platform/audit",
        permission: platformPermissions.auditView,
      },
      {
        key: "platform.exports",
        labelKey: "nav.exports",
        icon: "download",
        path: "/platform/exports",
        permission: platformPermissions.tenantExport,
      },
      {
        key: "platform.status",
        labelKey: "nav.systemStatus",
        icon: "sys-monitor",
        path: "/platform/status",
        public: true,
      },
    ],
  },
];

export const tenantNavigation: readonly NavigationEntry[] = tenantGroups.map(nestSubGroups);

/** Operator console (non-tenant host, platform operators only). */
export const consoleNavigation: readonly NavigationEntry[] = [
  {
    key: "operator",
    labelKey: "nav.operator",
    icon: "it-host",
    children: [
      {
        key: "operator.tenants",
        labelKey: "nav.tenants",
        icon: "building",
        path: "/operator/tenants",
        operatorOnly: true,
      },
      {
        key: "operator.platformSettings",
        labelKey: "nav.platformSettings",
        icon: "settings",
        path: "/operator/platform-settings",
        operatorOnly: true,
      },
      {
        key: "operator.status",
        labelKey: "nav.systemStatus",
        icon: "sys-monitor",
        path: "/platform/status",
        public: true,
      },
    ],
  },
];

export function navigationFor(mode: HostMode): readonly NavigationEntry[] {
  return mode.kind === "console" ? consoleNavigation : tenantNavigation;
}
